// ================== CORE IMPORTS ==================
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import React, { Suspense, lazy } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'

// ================== LAYOUT & COMPONENTS ==================
import { MobileLayout } from './components/MobileLayout'
import { LoginModal } from './components/LoginModal'

// ================== CONTEXTS ==================
import { StaffProvider } from './contexts/StaffContext'
import { ChatbotProvider } from './contexts/ChatbotContext'
import { PopupProvider } from './contexts/PopupContext'

// ================== FEATURES - MESSAGES ==================
import MessagesPage from './features/messages/routes/MessagesPage'
const ChatPage = lazy(() => import('./features/messages/routes/chat/ChatPage'))
const GroupChatDetail = lazy(() => import('./features/messages/routes/chat/GroupChatDetail'))

// ================== FEATURES - HOME ==================
const HomePage = lazy(() => import('./features/home/routes/HomePage'))

// ================== FEATURES - CUSTOMER MANAGEMENT ==================
const CustomerManagement = lazy(() => import('./features/customer-management/routes/CustomerManagementPage'))
const CustomerList = lazy(() => import('./features/customer-management/routes/CustomerListPage'))
const CustomerDetail = lazy(() => import('./features/customer-management/routes/CustomerDetailPage'))
const CustomerStats = lazy(() => import('./features/customer-management/routes/CustomerStatsPage'))

// ================== FEATURES - ACCOUNT ==================
const AccountPage = lazy(() => import('./features/account/routes/AccountPage'))
const ProfilePage = lazy(() => import('./features/account/routes/ProfilePage'))

// ================== CONFIGURATION ==================
const queryClient = new QueryClient()
const STORAGE_KEY = 'pangoconnect_user'

const App: React.FC = () => {
  const isLoggedIn = !!localStorage.getItem(STORAGE_KEY)
  
  return (
    <>
      <Toaster position="top-center" />
      <QueryClientProvider client={queryClient}>
        <Router>
          <StaffProvider>
            <ChatbotProvider>
              <PopupProvider>
                <Suspense 
                  fallback={
                    <div className="flex h-screen items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  }
                >
                  {!isLoggedIn && <LoginModal isOpen />}
                  <div style={{ filter: !isLoggedIn ? 'blur(2px)' : undefined }}>
                    <Routes>
                      {/* ================== STANDALONE ROUTES ================== */}
                      <Route path="/customers/list" element={<CustomerList />} />

                      {/* ================== MAIN LAYOUT ROUTES ================== */}
                      <Route path="/" element={<MobileLayout />}>
                        {/* Home */}
                        <Route index element={<HomePage />} />
                        
                        {/* Customer Management */}
                        <Route path="customers" element={<CustomerManagement />}>
                          <Route index element={<CustomerStats />} />
                          <Route path="all" element={<CustomerDetail />} />
                          <Route path="new" element={<CustomerDetail />} />
                          <Route path="vip" element={<CustomerDetail />} />
                          <Route path="loyal" element={<CustomerDetail />} />
                          <Route path="at-risk" element={<CustomerDetail />} />
                        </Route>

                        {/* Messages & Chat */}
                        <Route path="messages" element={<MessagesPage />} />
                        <Route path="messages/group/:id" element={<GroupChatDetail />} />
                        <Route path="messages/chat/:id" element={<ChatPage />} />

                        {/* Account Management */}
                        <Route path="account" element={<AccountPage />}>
                          <Route index element={<ProfilePage />} />
                        </Route>

                        {/* Fallback Route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Route>
                    </Routes>
                  </div>
                </Suspense>
              </PopupProvider>
            </ChatbotProvider>
          </StaffProvider>
        </Router>
      </QueryClientProvider>
    </>
  )
}

export default App 